/**
 * KUHAS student view — list the CURRENTLY AUTHENTICATED user's enrolled
 * offerings and consume the assembled lesson/asset content, marking
 * progress. Route: `/kuhas/student` (no query params).
 *
 * SECURITY: student identity is derived server-side from the JWT
 * (`/api/kuhas/enrollments/me`). This page never accepts a `student_id`
 * from the URL — that was an IDOR vector.
 */
import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () =>
  localStorage.getItem("kcis_admin_token") || localStorage.getItem("admin_token") || "";
const auth = () => ({ Authorization: `Bearer ${getToken()}` });

export default function KuhasStudentView() {
  const [enrollments, setEnrollments] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [content, setContent] = useState(null);
  const [progress, setProgress] = useState({});
  const [loaded, setLoaded] = useState(false);

  const token = getToken();

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/kuhas/enrollments/me`, { headers: auth() });
      setEnrollments(r.data.items || []);
      setStudentId(r.data.student_id || "");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to load enrollments");
    } finally {
      setLoaded(true);
    }
  }, []);
  useEffect(() => { if (token) load(); else setLoaded(true); }, [load, token]);

  async function openOffering(en) {
    setSelectedId(en._id);
    try {
      const r = await axios.get(`${API}/kuhas/offerings/${en.offering_id}/content`, { headers: auth() });
      setContent(r.data);
      const map = {};
      (en.progress?.lessons_completed || []).forEach(id => { map[id] = true; });
      setProgress(map);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail?.message : (detail || "Failed to open"));
    }
  }
  async function markDone(assetId) {
    if (!selectedId) return;
    try {
      await axios.post(`${API}/kuhas/enrollments/${selectedId}/progress`,
        { lesson_completed: assetId }, { headers: auth() });
      setProgress({ ...progress, [assetId]: true });
      toast.success("Progress saved");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail?.message : (detail || "Failed"));
    }
  }

  if (!token) {
    return <Navigate to="/admin/login?next=/kuhas/student" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="kuhas-student">
      <div className="max-w-4xl mx-auto">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">KUHAS · Student</div>
        <h1 className="font-serif text-2xl text-slate-900">Your Enrollments</h1>
        {studentId && (
          <p className="text-xs text-slate-500 mb-5" data-testid="stu-identity">
            Signed in as <code>{studentId}</code>
          </p>
        )}

        <section className="bg-white border border-slate-200 rounded-2xl p-4 mb-5" data-testid="stu-enroll-list">
          {loaded && enrollments.length === 0 && <div className="text-sm text-slate-500">No enrollments yet.</div>}
          <ul className="space-y-1.5 text-sm">
            {enrollments.map(en => (
              <li key={en._id}>
                <button data-testid={`stu-open-${en._id}`}
                  onClick={() => openOffering(en)}
                  className={`w-full text-left rounded-lg px-3 py-2 border ${selectedId === en._id ? "bg-purple-50 border-purple-300" : "border-slate-100"}`}>
                  <b>{en.offering?.cohort_name || "(cohort)"}</b>
                  <span className="ml-2 text-xs text-slate-500">
                    MC v{en.offering?.master_course_version} · status: {en.status}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        {content && (
          <section className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="stu-content">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">
              Assembled lessons · voice locked at {content.voice_profile?.voice_model_ref || "—"}
            </div>
            <ol className="space-y-1.5">
              {content.sequence.map(s => {
                const done = !!progress[s.asset._id];
                return (
                  <li key={s.link_id} className="border border-slate-100 rounded-lg px-3 py-2 flex items-start justify-between">
                    <div>
                      <span className="font-mono text-xs text-slate-500 mr-2">#{s.sequence_position}</span>
                      <b>{s.asset.title}</b>
                      <span className="ml-2 text-xs text-slate-500">{s.asset.type} · {s.asset.complexity_level}</span>
                    </div>
                    <button data-testid={`stu-mark-${s.asset._id}`}
                      onClick={() => markDone(s.asset._id)}
                      disabled={done}
                      className={`text-xs px-3 py-1.5 rounded-full ${done ? "bg-emerald-100 text-emerald-800" : "bg-purple-900 text-white"}`}>
                      {done ? <span><Check className="w-3 h-3 inline mr-1" />Done</span> : "Mark complete"}
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
        )}
      </div>
    </div>
  );
}
